import Button from '@repo/ui/button'
import RadioInput from '@repo/ui/radio-input'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { getIdDocumentWarnings } from '../../../api/affected-staffs/get-id-document-warnings'
import { reassignStaffRequest } from '../../../api/affected-staffs/reassign-staff-request'
import { API } from '../../../api/apis.config'
import { getPeriods } from '../../../api/periods/get-periods'
import { StaffRequest } from '../../../lib/types/models/staff-request/staff-request'
import { StaffRequestFilters } from '../../../lib/types/models/staff-request/staff-request-input'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { staffRequestColumns } from '../../../views/staffs/utils'
import DynamicDataTable from '../../elements/dynamic-table'
import IdDocumentWarningsModal from './id-document-warnings-modal'

type Props = {
  affectedStaffId: number
  staffId: number
  // Contract end date of the affectation, used when the target staff request has no end date
  endDate: string
  className?: string
  filters?: StaffRequestFilters
  onCancel?: () => void
  onSuccess?: () => void
}

const ReassignStaffRequest = ({
  className,
  filters,
  affectedStaffId,
  staffId,
  endDate,
  onSuccess,
  onCancel
}: Props) => {
  const [selectedStaffRequest, setSelectedStaffRequest] = useState<number>()
  // Identity document warnings waiting to be acknowledged before the reassignment
  const [idDocumentWarnings, setIdDocumentWarnings] = useState<string[]>([])

  const reassignStaffRequestMutation = useMutation({
    mutationFn: reassignStaffRequest,
    onSuccess: () => {
      setIdDocumentWarnings([])
      toast.success('Affectation réussie')
      onSuccess?.()
      onCancel?.()
    },
    onError: showAxiosError
  })

  const idDocumentWarningsMutation = useMutation({
    mutationFn: getIdDocumentWarnings,
    onError: showAxiosError
  })

  const { data: periods } = useQuery({
    queryKey: ['periods', 'LIGHT'],
    queryFn: () => getPeriods({ version: 'LIGHT' })
  })

  return (
    <div className="divide-y divide-white-active">
      <div className={className}>
        <DynamicDataTable<StaffRequest, StaffRequestFilters>
          title={'Toutes les demandes'}
          columns={[
            {
              accessorKey: 'id',
              header: '',
              cell: ({ row }) => (
                <RadioInput
                  checked={selectedStaffRequest === row.original.id}
                  onCheck={() => setSelectedStaffRequest(row.original.id)}
                />
              )
            },
            ...staffRequestColumns(periods?.data ?? [])
          ]}
          headerType="light"
          allowSearchParam
          filters={filters}
          endpoint={API.staffRequest.getAll.url}
          permissions={{ delete: false }}
        />
      </div>
      <div className="py-6 px-4">
        <div className="flex justify-end w-full gap-2.5 ">
          <Button variant="ghost" onClick={onCancel} label="Fermer" />
          <Button
            variant="primary"
            loading={
              reassignStaffRequestMutation.isPending || idDocumentWarningsMutation.isPending
            }
            onClick={() => {
              if (!selectedStaffRequest) return
              idDocumentWarningsMutation.mutate(
                { staffId, staffRequestId: selectedStaffRequest, endDate },
                {
                  onSuccess: ({ warnings }) => {
                    if (warnings.length) setIdDocumentWarnings(warnings)
                    else
                      reassignStaffRequestMutation.mutate({
                        staffRequestId: selectedStaffRequest,
                        affectedStaffId
                      })
                  }
                }
              )
            }}
            label="Suivant"
          />
        </div>
      </div>
      <IdDocumentWarningsModal
        isOpen={idDocumentWarnings.length > 0}
        warnings={idDocumentWarnings}
        isLoading={reassignStaffRequestMutation.isPending}
        onCancel={() => setIdDocumentWarnings([])}
        onConfirm={() => {
          if (selectedStaffRequest)
            reassignStaffRequestMutation.mutate({
              staffRequestId: selectedStaffRequest,
              affectedStaffId
            })
        }}
      />
    </div>
  )
}

export default ReassignStaffRequest
